import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MessageCircle,
  Instagram,
  Facebook,
  Globe,
  Star,
  MoreHorizontal,
  Building2,
  MapPin,
} from "lucide-react";
import { cn } from "@/utils/cn";
import type { Contact } from "./ContactsPage";

const channelIcons = {
  whatsapp: { icon: MessageCircle, color: "#22C55E", label: "WhatsApp" },
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  widget: { icon: Globe, color: "#2563EB", label: "Chat Widget" },
};

const statusStyles = {
  customer: { bg: "bg-green-50", text: "text-green-600", label: "Cliente" },
  lead: { bg: "bg-amber-50", text: "text-amber-600", label: "Lead" },
  active: { bg: "bg-blue-50", text: "text-blue-600", label: "Ativo" },
  inactive: { bg: "bg-gray-50", text: "text-gray-500", label: "Inativo" },
};

const tagColors: Record<string, { bg: string; text: string }> = {
  VIP: { bg: "bg-purple-100", text: "text-purple-700" },
  Enterprise: { bg: "bg-indigo-100", text: "text-indigo-700" },
  Parceiro: { bg: "bg-cyan-100", text: "text-cyan-700" },
  Lead: { bg: "bg-amber-100", text: "text-amber-700" },
  Startup: { bg: "bg-pink-100", text: "text-pink-700" },
  Premium: { bg: "bg-yellow-100", text: "text-yellow-700" },
  Agência: { bg: "bg-teal-100", text: "text-teal-700" },
  "E-commerce": { bg: "bg-orange-100", text: "text-orange-700" },
  Tech: { bg: "bg-blue-100", text: "text-blue-700" },
  Consultoria: { bg: "bg-rose-100", text: "text-rose-700" },
  Marketing: { bg: "bg-fuchsia-100", text: "text-fuchsia-700" },
  Logística: { bg: "bg-lime-100", text: "text-lime-700" },
  Fintech: { bg: "bg-emerald-100", text: "text-emerald-700" },
  Indústria: { bg: "bg-slate-100", text: "text-slate-700" },
};

interface ContactCardProps {
  contact: Contact;
  delay?: number;
  onToggleFavorite: (id: string) => void;
  onSelect: (contact: Contact) => void;
  isSelected?: boolean;
}

export function ContactCard({
  contact,
  delay = 0,
  onToggleFavorite,
  onSelect,
  isSelected = false,
}: ContactCardProps) {
  const ChannelIcon = channelIcons[contact.channel].icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -4 }}
      onClick={() => onSelect(contact)}
      className={cn(
        "group cursor-pointer rounded-[14px] bg-white p-5 shadow-sm border transition-all duration-200",
        isSelected
          ? "border-[#2563EB] ring-2 ring-[#2563EB]/20"
          : "border-[#E2E8F0] hover:border-[#CBD5E1] hover:shadow-md"
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={contact.avatar}
              alt={contact.name}
              className="h-12 w-12 rounded-full object-cover ring-2 ring-white"
            />
            <div
              className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white"
              style={{ backgroundColor: channelIcons[contact.channel].color }}
            >
              <ChannelIcon className="h-2.5 w-2.5 text-white" />
            </div>
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-[#0F172A] truncate">
              {contact.name}
            </h3>
            <p className="text-xs text-[#64748B] truncate">{contact.role}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(contact.id);
            }}
            className={cn(
              "rounded-lg p-1.5 transition-colors",
              contact.isFavorite
                ? "text-amber-400 hover:text-amber-500"
                : "text-[#CBD5E1] hover:text-amber-400"
            )}
          >
            <Star
              className="h-4 w-4"
              fill={contact.isFavorite ? "currentColor" : "none"}
            />
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="rounded-lg p-1.5 text-[#94A3B8] hover:bg-[#F1F5F9] hover:text-[#64748B]"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Company & Location */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{contact.company}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{contact.location}</span>
        </div>
      </div>

      {/* Tags */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-medium",
            statusStyles[contact.status].bg,
            statusStyles[contact.status].text
          )}
        >
          {statusStyles[contact.status].label}
        </span>
        {contact.tags.slice(0, 2).map((tag) => (
          <span
            key={tag}
            className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              tagColors[tag]?.bg || "bg-gray-100",
              tagColors[tag]?.text || "text-gray-600"
            )}
          >
            {tag}
          </span>
        ))}
        {contact.tags.length > 2 && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            +{contact.tags.length - 2}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between border-t border-[#F1F5F9] pt-4">
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => e.stopPropagation()}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F8FAFC] text-[#64748B] transition-colors hover:bg-[#2563EB] hover:text-white"
          >
            <Phone className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F8FAFC] text-[#64748B] transition-colors hover:bg-[#2563EB] hover:text-white"
          >
            <Mail className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F8FAFC] text-[#64748B] transition-colors hover:bg-[#22C55E] hover:text-white"
          >
            <MessageCircle className="h-4 w-4" />
          </button>
        </div>

        <div className="text-right">
          <p className="text-xs text-[#94A3B8]">Último contato</p>
          <p className="text-xs font-medium text-[#64748B]">{contact.lastContact}</p>
        </div>
      </div>
    </motion.div>
  );
}